import { Injectable } from "@angular/core";
import { Location } from '@angular/common';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot
} from "@angular/router";
import { Observable, of as observableOf } from "rxjs";
import { skipWhile, switchMap, first, tap } from 'rxjs/operators';
import { InitializationStatusCodes, Role } from "portalServices";
import { appRouteConstants } from '../../../app.route.constants';
import { AppServices } from 'app/servicelayer/app-services';
import { RouteAuthenticationService } from '../../../common/services/route-auth.service';
import { AppInitializationService } from '../../../common/services/app-initialization.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationGuard implements CanActivate
{
    constructor(
        private appServices: AppServices,
        private router: Router,
        private angularLocation: Location,
        private routeAuthService: RouteAuthenticationService,
        private appInitService: AppInitializationService
    ) {}

    public get authenticationService() {
        return this.appServices.authenticationService;
    }

    public get initializationService() {
        return this.appServices.initializationService;
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean>
    {
        console.log('AuthenticationGuard: Checking access to:', state.url);

        // Immediately allow join routes without any authentication checks
        if (state.url.includes('join')) {
            console.log('AuthenticationGuard: Join route detected - allowing immediate access');
            return observableOf(true);
        }

        return this.initializationService.initState
            .pipe(
                skipWhile((data: any) => !this.appIsReady(data)),
                tap(initState => console.log('AuthenticationGuard: App ready with state:', initState)),
                switchMap(() => this.performAuthenticationCheck(route, state)),
                first(),
                tap(result => {
                    // Safety net: Ensure spinner is reset after guard completes
                    // This prevents infinite spinner when guard logic completes but 
                    // counter may be misaligned due to initialization calls
                    if (result && !state.url.includes('login') && !state.url.includes('join')) {
                        setTimeout(() => {
                            console.log('AuthenticationGuard: Access check complete for:', state.url);
                        }, 100);
                    }
                })
            ) as any as Observable<boolean>;
    }

    private appIsReady(initState: string): boolean
    {
        return initState === InitializationStatusCodes.RUNNING
            || initState === InitializationStatusCodes.UNAUTHENTICATED;
    }

    private performAuthenticationCheck(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean>
    {
        const loginRoute = state.url.includes(appRouteConstants.AUTH.LOGIN);
        const resetPasswordRoute = state.url.includes(appRouteConstants.AUTH.RESET);
        const resetRequestRoute = state.url.includes(appRouteConstants.AUTH.RESET_REQUEST);
        const joinRoute = state.url.includes('join');

        console.log('AuthenticationGuard: Checking route access for:', state.url);
        console.log('AuthenticationGuard: Route checks - login:', loginRoute, 'reset:', resetPasswordRoute, 'resetReq:', resetRequestRoute, 'join:', joinRoute);

        // Allow access to authentication routes
        if (loginRoute || resetPasswordRoute || resetRequestRoute || joinRoute)
        {
            console.log('AuthenticationGuard: Allowing access to public route:', state.url);
            return observableOf(true);
        }

        // Only disable auto-redirect for specific routes, NOT for root routes
        const isRootRoute = ['/', '', '/hr'].includes(state.url);
        if (!isRootRoute) {
            console.log('AuthenticationGuard: Disabling auto-redirect for specific protected route:', state.url);
            this.appInitService.disableAutoRedirect();
        } else {
            console.log('AuthenticationGuard: Root route detected, keeping auto-redirect enabled for:', state.url);
        }

        // Check if user is authenticated
        const isAuthenticated = this.authenticationService.isAuthenticated();
        console.log('AuthenticationGuard: User authenticated?', isAuthenticated);
        
        if (!isAuthenticated) {
            console.log('AuthenticationGuard: User not authenticated, redirecting to login');
            this.router.navigate([appRouteConstants.AUTH.LOGIN], {
                queryParams: { returnUrl: state.url }
            });
            return observableOf(false);
        }

        // Get required roles from route data
        const requiredRoles = route.data?.roles as Role[];

        if (requiredRoles && requiredRoles.length > 0) {
            const hasRequiredRole = requiredRoles.some(role => 
                this.authenticationService.hasRole(role)
            );

            if (!hasRequiredRole) {
                console.log('AuthenticationGuard: User lacks required roles for:', state.url);
                console.log('Required roles:', requiredRoles);
                console.log('User roles:', this.getUserRoles());

                // Prevent infinite redirect loop: if trying to access dashboard without roles, logout instead
                if (state.url.includes(appRouteConstants.DASHBOARD)) {
                    console.log('AuthenticationGuard: User lacks roles for dashboard, logging out to prevent redirect loop');
                    this.authenticationService.logout();
                    this.router.navigate([appRouteConstants.AUTH.LOGIN], {
                        queryParams: { message: 'insufficient_permissions' }
                    });
                } else {
                    // For other protected routes, redirect to dashboard (or login if dashboard also fails)
                    console.log('AuthenticationGuard: Redirecting to dashboard');
                    this.router.navigate([appRouteConstants.DASHBOARD]);
                }
                return observableOf(false);
            }
        }

        console.log('AuthenticationGuard: Access granted to:', state.url);
        return observableOf(true);
    }

    private getUserRoles(): Role[] {
        try {
            const session = (this.authenticationService as any).session;
            return session?.roles || [];
        } catch (error) {
            console.warn('AuthenticationGuard: Could not get user roles:', error);
            return [];
        }
    }
}